/**
 * Local cache of previously scanned barcode products.
 * Persisted to localStorage so scanned foods appear in search results
 * across sessions.
 */

import type { FSANZFood, NutrientsPer100g } from './fsanz-types'
import type { BarcodeProduct } from '../services/barcodeService'

const STORAGE_KEY = 'scannedProductsCache'
const BACKFILL_KEY = 'scannedProductsCacheBackfilled'
const MAX_ITEMS = 50

interface CachedProduct {
  barcode: string
  productName: string
  brand?: string
  per100g: NutrientsPer100g
  savedAt: number
}

function loadCache(): CachedProduct[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as CachedProduct[]
  } catch {
    return []
  }
}

function saveCache(items: CachedProduct[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

/** Save a scanned product to the local cache (most recent first, max 50). */
export function saveScannedProduct(product: BarcodeProduct): void {
  const items = loadCache().filter((c) => c.barcode !== product.barcode)
  items.unshift({
    barcode: product.barcode,
    productName: product.productName,
    brand: product.brand,
    per100g: product.per100g,
    savedAt: Date.now(),
  })
  saveCache(items.slice(0, MAX_ITEMS))
}

/** Return all cached scanned products as FSANZFood objects (id = barcode). */
export function getScannedFoods(): FSANZFood[] {
  return loadCache().map((c) => ({
    id: c.barcode,
    // Include brand in name so typing the brand also matches
    name: c.brand ? `${c.productName} (${c.brand})` : c.productName,
    // shortName shown in search results — just the product name
    shortName: c.productName,
    per100g: c.per100g,
  }))
}

/**
 * One-time backfill: scans all existing meal log entries in localStorage
 * and saves any barcode-scanned foods (those with inline per100g) to the cache.
 * Safe to call on every app start — skips if already done.
 */
export function backfillScannedProductsCache(): void {
  try {
    if (localStorage.getItem(BACKFILL_KEY)) return

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith('energymatch-daily-logs-')) continue

      const raw = localStorage.getItem(key)
      if (!raw) continue

      const logs = JSON.parse(raw) as Record<string, Record<string, { foodEntries?: Array<{ foodId: string; amountGrams: number; per100g?: NutrientsPer100g; productName?: string }> }>>
      for (const day of Object.values(logs)) {
        for (const slot of Object.values(day)) {
          for (const fe of slot.foodEntries ?? []) {
            if (fe.per100g && fe.productName) {
              saveScannedProduct({ barcode: fe.foodId, productName: fe.productName, per100g: fe.per100g })
            }
          }
        }
      }
    }

    localStorage.setItem(BACKFILL_KEY, '1')
  } catch {
    // Non-critical — silently skip
  }
}
